import {
  Globe,
  Terminal,
  FileText,
  Database,
  BarChart,
  Code2,
  Server,
  Table,
  GitMerge,
  MessageSquare,
} from 'lucide-react'
import type {
  Agent,
  Workflow,
  LogEntry,
  Prompt,
  ContextProfile,
  ToolDefinition,
  ToolCall,
} from './types'

export const MOCK_PROFILES: ContextProfile[] = [
  {
    id: 'cp1',
    name: 'Frontend React',
    strategy: 'DEPENDENCY',
    globs: ['**/*.tsx', '**/*.ts'],
  },
  {
    id: 'cp2',
    name: 'Backend API',
    strategy: 'PATTERN',
    globs: ['src/controllers/*.ts'],
  },
  {
    id: 'cp3',
    name: 'Documentation',
    strategy: 'PATTERN',
    globs: ['docs/**/*.md'],
  },
]

export const MOCK_TOOLS_DEFINITIONS: ToolDefinition[] = [
  {
    id: 'WEB_SEARCH',
    name: 'Google Search',
    description: 'Access real-time information from the web via Google Search API.',
    type: 'NATIVE',
    category: 'Network',
    group: 'Native Capabilities',
    icon: Globe,
  },
  {
    id: 'CODE_EXECUTION',
    name: 'Code Sandbox',
    description: 'Execute Python and JavaScript code in a secure, isolated sandbox.',
    type: 'NATIVE',
    category: 'Compute',
    group: 'Native Capabilities',
    icon: Terminal,
  },
  {
    id: 'FILE_SYSTEM',
    name: 'File System',
    description: 'Read and write access to the project file system.',
    type: 'NATIVE',
    category: 'System',
    group: 'Native Capabilities',
    icon: FileText,
  },
  {
    id: 'KNOWLEDGE_RETRIEVAL',
    name: 'RAG Retrieval',
    description: 'Semantic search over project documentation and code.',
    type: 'NATIVE',
    category: 'Data',
    group: 'Native Capabilities',
    icon: Database,
  },
  {
    id: 'py_pandas_analyze',
    name: 'Pandas Analyze',
    description: 'Generate statistical summaries of CSV/JSON data.',
    type: 'PYTHON_FUNCTION',
    category: 'Compute',
    group: 'Data Science Utils',
    icon: Table,
  },
  {
    id: 'py_plot_generator',
    name: 'Matplotlib Plot',
    description: 'Generate charts and graphs from datasets.',
    type: 'PYTHON_FUNCTION',
    category: 'Compute',
    group: 'Data Science Utils',
    icon: BarChart,
  },
  {
    id: 'mcp_gh_list_prs',
    name: 'List PRs',
    description: 'Fetch open pull requests from the connected repository.',
    type: 'MCP_TOOL',
    category: 'Network',
    group: 'GitHub Integration',
    icon: Code2,
  },
  {
    id: 'mcp_gh_create_issue',
    name: 'Create Issue',
    description: 'Create a new issue in the repository.',
    type: 'MCP_TOOL',
    category: 'Network',
    group: 'GitHub Integration',
    icon: GitMerge,
  },
  {
    id: 'mcp_pg_query',
    name: 'Run Query',
    description: 'Execute read-only SQL queries against the production DB.',
    type: 'MCP_TOOL',
    category: 'Data',
    group: 'PostgreSQL MCP',
    icon: Server,
  },
  {
    id: 'mcp_pg_schema',
    name: 'Get Schema',
    description: 'Retrieve current table definitions and relationships.',
    type: 'MCP_TOOL',
    category: 'Data',
    group: 'PostgreSQL MCP',
    icon: Database,
  },
  {
    id: 'mcp_slack_send',
    name: 'Send Message',
    description: 'Post a message to a specific Slack channel.',
    type: 'MCP_TOOL',
    category: 'Network',
    group: 'Slack MCP',
    icon: MessageSquare,
  },
  // Backend Improvement Pipeline tools (Python functions)
  {
    id: 'scan_for_improvements',
    name: 'Scan for Improvements',
    description:
      'Scan a codebase for potential improvements, bugs, or features and create workflow items for review.',
    type: 'PYTHON_FUNCTION',
    category: 'Compute',
    group: 'Improvement Pipeline',
    icon: Code2,
    bindingKind: 'python',
    bindingId: 'scan_for_improvements',
  },
  {
    id: 'frontend_refactor_analysis',
    name: 'Frontend Refactor Analysis',
    description:
      'Run a static analysis of the frontend feature tree and produce a structured refactor report.',
    type: 'PYTHON_FUNCTION',
    category: 'Compute',
    group: 'Improvement Pipeline',
    icon: Code2,
    bindingKind: 'python',
    bindingId: 'frontend_refactor_analysis',
  },
  {
    id: 'list_workflows',
    name: 'List Workflows',
    description: 'List workflows by status, type, priority, or other filters.',
    type: 'PYTHON_FUNCTION',
    category: 'System',
    group: 'Improvement Pipeline',
    icon: Database,
    bindingKind: 'python',
    bindingId: 'list_workflows',
  },
  {
    id: 'approve_workflow',
    name: 'Approve Workflow',
    description: 'Approve a pending workflow item so it can be executed by agents.',
    type: 'PYTHON_FUNCTION',
    category: 'System',
    group: 'Improvement Pipeline',
    icon: Database,
    bindingKind: 'python',
    bindingId: 'approve_workflow',
  },
  {
    id: 'reject_workflow',
    name: 'Reject Workflow',
    description: 'Reject a pending workflow item and record an optional reason.',
    type: 'PYTHON_FUNCTION',
    category: 'System',
    group: 'Improvement Pipeline',
    icon: Database,
    bindingKind: 'python',
    bindingId: 'reject_workflow',
  },
  {
    id: 'execute_workflow',
    name: 'Execute Workflow',
    description:
      'Execute a pending workflow to implement suggested changes, with optional dry-run support.',
    type: 'PYTHON_FUNCTION',
    category: 'System',
    group: 'Improvement Pipeline',
    icon: Server,
    bindingKind: 'python',
    bindingId: 'execute_workflow',
  },
]

export const MOCK_AGENTS: Agent[] = [
  {
    id: 'a1',
    name: 'Atlas',
    role: 'Architect',
    status: 'BUSY',
    avatar: 'AT',
    model: 'gemini-3-pro-preview',
    systemPrompt:
      'You are a senior software architect. Focus on scalability, patterns, and system design.',
    tools: ['KNOWLEDGE_RETRIEVAL', 'FILE_SYSTEM', 'mcp_gh_list_prs'],
    contextProfileId: 'cp2',
  },
  {
    id: 'a2',
    name: 'Hermes',
    role: 'Messenger',
    status: 'IDLE',
    avatar: 'HE',
    model: 'claude-sonnet-4-6',
    systemPrompt:
      'You are a communication specialist. Summarize technical logs into readable updates.',
    tools: ['WEB_SEARCH', 'mcp_slack_send'],
    contextProfileId: 'cp3',
  },
  {
    id: 'a3',
    name: 'Hephaestus',
    role: 'Builder',
    status: 'IDLE',
    avatar: 'HP',
    model: 'claude-sonnet-4-6',
    systemPrompt:
      'You are a build engineer. Fix compilation errors and manage dependencies.',
    tools: ['CODE_EXECUTION', 'FILE_SYSTEM', 'py_pandas_analyze'],
    contextProfileId: 'cp1',
  },
]

export const MOCK_WORKFLOWS: Workflow[] = [
  {
    id: 'wf-1024',
    title: 'Refactor Auth Service',
    status: 'EXECUTING',
    agentId: 'a1',
    currentStep: 'Analyzing JWT middleware',
    lastLog: 'Parsing token structure...',
    createdAt: '2023-10-27T10:00:00Z',
  },
  {
    id: 'wf-1025',
    title: 'Generate Unit Tests',
    status: 'THINKING',
    agentId: 'a2',
    currentStep: 'Reading component tree',
    lastLog: 'Identifying public methods...',
    createdAt: '2023-10-27T10:05:00Z',
  },
  {
    id: 'wf-1026',
    title: 'Database Migration',
    status: 'PENDING',
    createdAt: '2023-10-27T11:00:00Z',
  },
  {
    id: 'wf-1023',
    title: 'Update Documentation',
    status: 'DONE',
    agentId: 'a3',
    currentStep: 'Completed',
    lastLog: 'Wiki updated successfully.',
    createdAt: '2023-10-27T09:00:00Z',
  },
]

export const MOCK_LOGS: LogEntry[] = [
  {
    id: 'l1',
    timestamp: '10:42:01',
    agentId: 'Atlas',
    level: 'INFO',
    message: 'Initializing analysis engine...',
  },
  {
    id: 'l2',
    timestamp: '10:42:02',
    agentId: 'Atlas',
    level: 'DEBUG',
    message: 'Loaded 42 context files.',
  },
  {
    id: 'l3',
    timestamp: '10:42:05',
    agentId: 'Hermes',
    level: 'INFO',
    message: 'WebSocket connection established.',
  },
  {
    id: 'l4',
    timestamp: '10:42:10',
    agentId: 'Atlas',
    level: 'WARN',
    message: 'High token usage detected in context window.',
  },
]

// TODO(scan): Dead code — MOCK_PROMPTS is never imported anywhere, safe to delete
// MOCK_PROMPTS is kept for reference only. Prompts now come from the API via agentCatalogSlice.fetchPrompts().
export const MOCK_PROMPTS: Prompt[] = [
  {
    id: 1,
    name: 'Code Reviewer Identity',
    description:
      'Standard procedure for reviewing Pull Requests focusing on security and style.',
    tags: ['Code', 'Security', 'Review'],
    version: 12,
    type: 'system',
    is_builtin: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    content: `You are an expert code reviewer...

Review Constraints:
- Check for SQL Injection risks
- Ensure strict typing (TypeScript)
- Verify error handling patterns`,
  },
  {
    id: 2,
    name: 'SQL Optimizer Task',
    description: 'Analyzes raw SQL queries and suggests indexing strategies.',
    tags: ['Database', 'Performance'],
    version: 4,
    type: 'general',
    is_builtin: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    content: 'Analyze the following query plan and suggest indexes...',
  },
  {
    id: 3,
    name: 'Sr. React Dev Identity',
    description: 'Persona for generating high-quality React components.',
    tags: ['Frontend', 'React'],
    version: 2,
    type: 'system',
    is_builtin: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    content:
      'You are a senior React developer. You prefer functional components and Tailwind CSS...',
  },
]

export const MOCK_TOOL_CALLS: ToolCall[] = [
  {
    id: 'tc-1',
    agentId: 'a1',
    toolId: 'KNOWLEDGE_RETRIEVAL',
    timestamp: '10:42:15',
    status: 'COMPLETED',
    durationMs: 450,
    args: { query: 'JWT authentication patterns' },
    result: { documents: ['auth_service.ts', 'middleware.ts'], score: 0.95 },
  },
  {
    id: 'tc-2',
    agentId: 'a1',
    toolId: 'mcp_gh_list_prs',
    timestamp: '10:43:00',
    status: 'COMPLETED',
    durationMs: 1200,
    args: { state: 'open', label: 'security' },
    result: [{ id: 403, title: 'Fix JWT Validation', author: 'dependabot' }],
  },
  {
    id: 'tc-3',
    agentId: 'a3',
    toolId: 'py_pandas_analyze',
    timestamp: '10:45:22',
    status: 'FAILED',
    durationMs: 230,
    args: { dataframe_id: 'df_metrics', method: 'correlate' },
    result: 'Error: Dataframe ID not found in context.',
  },
  {
    id: 'tc-4',
    agentId: 'a2',
    toolId: 'mcp_slack_send',
    timestamp: '10:46:10',
    status: 'RUNNING',
    durationMs: 0,
    args: { channel: '#dev-ops', message: 'Deployment verified.' },
    result: null,
  },
  {
    id: 'tc-5',
    agentId: 'a3',
    toolId: 'CODE_EXECUTION',
    timestamp: '10:41:05',
    status: 'COMPLETED',
    durationMs: 800,
    args: { language: 'python', code: 'print("Hello World")' },
    result: { stdout: 'Hello World', stderr: '', exit_code: 0 },
  },
]

